import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withModifiers as _withModifiers, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, mergeProps as _mergeProps, normalizeStyle as _normalizeStyle, isRef as _isRef, withKeys as _withKeys } from "vue";
const _hoisted_1 = ["src"];
const _hoisted_2 = {
    key: 2,
    class: "ai-chat-text expanded-title"
};
const _hoisted_3 = ["src"];
const _hoisted_4 = ["innerHTML"];
const _hoisted_5 = { key: 0 };
const _hoisted_6 = {
    key: 1,
    class: "prompt-chips"
};
const _hoisted_7 = ["onClick"];
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import { useDarkMode } from '@/composables/useDarkMode';
import { useChat } from '@/composables/useChat';
import { useChatPanel } from '@/composables/useChatPanel';
export default /*@__PURE__*/ _defineComponent({
    __name: 'AiChatBanner',
    props: {
        examples: { default: undefined },
        prompts: { default: undefined },
        dynamicSuggestions: { type: Boolean, default: false },
        context: { default: '' }
    },
    setup(__props, { expose: __expose }) {
        const route = useRoute();
        const { isDarkMode } = useDarkMode();
        const { sendSessionlessChatMessage } = useChat();
        const props = __props;
        const resolvedExamples = computed(() => {
            return props.prompts ?? props.examples ?? route.meta.aiExamples;
        });
        const suggestedFollowUps = ref([]);
        const suggestRequestId = ref(0);
        const awaitingSuggestions = ref(false);
        function fetchSuggestions(metaPrompt) {
            if (!props.dynamicSuggestions)
                return;
            suggestedFollowUps.value = [];
            awaitingSuggestions.value = true;
            const reqId = ++suggestRequestId.value;
            sendSessionlessChatMessage(metaPrompt, (res) => {
                if (reqId !== suggestRequestId.value)
                    return;
                awaitingSuggestions.value = false;
                try {
                    const body = res?.body ?? '';
                    const match = body.match(/\[[\s\S]*\]/);
                    if (!match)
                        return;
                    const parsed = JSON.parse(match[0]);
                    if (Array.isArray(parsed)) {
                        suggestedFollowUps.value = parsed
                            .filter((s) => typeof s === 'string' && s.trim().length > 0)
                            .slice(0, 3);
                        scrollToEnd();
                    }
                }
                catch (e) {
                    // silently ignore — chips just won't appear this turn
                }
            });
        }
        function requestFollowUpSuggestions(assistantReply) {
            fetchSuggestions(`The assistant just replied to the user with: """${assistantReply}""". ` +
                `Suggest up to 3 short follow-up questions or replies the user might send next. ` +
                `Include Yes/No if the assistant's reply invites that. ` +
                `Don't include please or similar politeness markers. ` +
                `Each must be under 8 words. Respond ONLY with a JSON array of strings, no prose, no markdown.`);
        }
        function requestInitialSuggestions() {
            const topicHint = (props.prompts && props.prompts.length > 0)
                ? `The page they are on covers topics like: ${props.prompts.join('; ')}. `
                : '';
            const contextHint = props.context
                ? `User-specific context (treat as authoritative facts about the user's current situation): ${props.context}. ` +
                    `Strict rules: (1) Items listed as "on file" or with values are things the user ALREADY HAS — never ask when they will receive, get, or arrive. ` +
                    `(2) NEVER suggest questions about documents, forms, accounts, or items not present in this context. ` +
                    `(3) If the context says the user does not have something, do not ask about it. ` +
                    `(4) The topic hints below are just themes for the page — they are NOT statements about what the user has or wants. `
                : '';
            fetchSuggestions(`A user is opening a financial assistant chat. ${contextHint}${topicHint}` +
                `Suggest up to 3 short opening questions they might ask, grounded in the user-specific context. ` +
                `Prefer questions about understanding, interpreting, or acting on what they already have. ` +
                `Do NOT include "Yes", "No", or any yes/no style replies — these are opening questions, not answers. ` +
                `Each must be under 8 words. Respond ONLY with a JSON array of strings, no prose, no markdown.`);
        }
        const showPromptChips = computed(() => {
            const last = messages.value[messages.value.length - 1];
            return !!last && last.direction === 'in';
        });
        const YES_NO_OPENERS = /\b(would you|do you|does (this|that|it)|did (this|that|it)|is (this|that|it)|was (this|that|it)|shall i|shall we|want me to|should i|should we|can i|can we|could i|could we|may i|are you|have you|has (this|that|it)|will you|would it|would that)\b/i;
        const activeChips = computed(() => {
            const last = messages.value[messages.value.length - 1];
            if (!last || last.direction !== 'in')
                return [];
            const body = last.body || '';
            const lastSentence = body.split(/(?<=[.!?])\s+/).pop() || body;
            const isYesNo = props.dynamicSuggestions && lastSentence.trim().endsWith('?') && YES_NO_OPENERS.test(lastSentence);
            if (suggestedFollowUps.value.length > 0) {
                return isYesNo ? ['Yes', 'No', ...suggestedFollowUps.value] : suggestedFollowUps.value;
            }
            if (isYesNo)
                return ['Yes', 'No'];
            if (awaitingSuggestions.value)
                return [];
            const conversationStarted = messages.value.some(m => m.direction === 'out');
            if (conversationStarted)
                return [];
            return props.prompts ?? [];
        });
        const expanded = ref(false);
        const cardShape = ref(false);
        const currentIndex = ref(0);
        let intervalId = null;
        const endRef = ref(null);
        const canvasRef = ref(null);
        const bannerRef = ref(null);
        const inputBarRef = ref(null);
        const initialised = ref(false);
        const { message, messages, typing, idlePromptShown, userHasTyped, hasUnsentMessages, pushDisclaimer, onKeyPress, showSentMessage, sendMessage, sendMessageAndWaitForResponse, formatMessage, applyMonetaryMask, resetInputIdleTimer, clearIdleTimer, attach, sendToWhatsApp, installIosScrollGuard, removeIosScrollGuard, } = useChatPanel({
            isOpen: () => expanded.value,
            scrollToEnd,
            onSend: () => { suggestedFollowUps.value = []; },
            onReply: (text) => { requestFollowUpSuggestions(text); },
        });
        function onBannerClick() {
            if (!expanded.value)
                expand();
        }
        let outsideClick = null;
        function expand() {
            if (expanded.value)
                return;
            expanded.value = true;
            cardShape.value = true;
            if (!initialised.value) {
                initChat();
                initialised.value = true;
            }
            idlePromptShown.value = false;
            userHasTyped.value = false;
            resetInputIdleTimer();
            scrollToEnd();
            installIosScrollGuard();
            outsideClick = (e) => {
                const target = e.target;
                if (!target || !bannerRef.value)
                    return;
                // Treat the whole AiChatCard (if the banner sits inside one) as "inside",
                // and ignore clicks in Vuetify's teleported overlays (the + menu).
                const root = bannerRef.value.closest('.ai-chat-card') ?? bannerRef.value;
                if (root.contains(target) || target.closest('.v-overlay-container'))
                    return;
                collapse();
            };
            document.addEventListener('click', outsideClick);
        }
        function collapse() {
            expanded.value = false;
            clearIdleTimer();
            removeIosScrollGuard();
            if (outsideClick) {
                document.removeEventListener('click', outsideClick);
                outsideClick = null;
            }
        }
        function openChat() {
            expand();
        }
        __expose({ openChat });
        function initChat() {
            pushDisclaimer();
            requestInitialSuggestions();
        }
        function onExpandedShown() {
            scrollToEnd();
            nextTick(() => {
                const el = inputBarRef.value;
                if (!el)
                    return;
                const rect = el.getBoundingClientRect();
                const buffer = 80;
                const overflow = rect.bottom + buffer - window.innerHeight;
                if (overflow > 0) {
                    window.scrollBy({ top: overflow, behavior: 'smooth' });
                }
            });
        }
        function scrollToEnd() {
            nextTick(() => {
                if (canvasRef.value && endRef.value) {
                    canvasRef.value.scrollTop = canvasRef.value.scrollHeight;
                }
            });
        }
        let addedNavHide = false;
        function onInputFocus() {
            document.body.classList.add('hide-nav-bar');
            addedNavHide = true;
            setTimeout(() => {
                inputBarRef.value?.scrollIntoView({ block: 'center', behavior: 'smooth' });
            }, 300);
        }
        function onInputBlur() {
            if (addedNavHide)
                document.body.classList.remove('hide-nav-bar');
            addedNavHide = false;
        }
        function sendPrompt(text) {
            if (typing.value)
                return;
            message.value = text;
            showSentMessage();
            sendMessageAndWaitForResponse(text);
            message.value = "";
            idlePromptShown.value = false;
            userHasTyped.value = true;
            resetInputIdleTimer();
        }
        onMounted(() => {
            if (resolvedExamples.value && resolvedExamples.value.length > 1) {
                intervalId = setInterval(() => {
                    currentIndex.value = (currentIndex.value + 1) % resolvedExamples.value.length;
                }, 3000);
            }
            watch(message, () => { resetInputIdleTimer(); });
            watch(() => [props.prompts, props.context], () => {
                if (!expanded.value || !initialised.value)
                    return;
                if (idlePromptShown.value)
                    return;
                const conversationStarted = messages.value.some(m => m.direction === 'out');
                if (conversationStarted)
                    return;
                requestInitialSuggestions();
            }, { deep: true });
        });
        onUnmounted(() => {
            if (intervalId)
                clearInterval(intervalId);
            clearIdleTimer();
            removeIosScrollGuard();
            if (addedNavHide)
                document.body.classList.remove('hide-nav-bar');
            if (outsideClick) {
                document.removeEventListener('click', outsideClick);
                outsideClick = null;
            }
        });
        return (_ctx, _cache) => {
            const _component_v_icon = _resolveComponent("v-icon");
            const _component_v_list_item_title = _resolveComponent("v-list-item-title");
            const _component_v_list_item = _resolveComponent("v-list-item");
            const _component_v_list = _resolveComponent("v-list");
            const _component_v_menu = _resolveComponent("v-menu");
            const _component_v_text_field = _resolveComponent("v-text-field");
            return (_openBlock(), _createElementBlock("div", {
                ref_key: "bannerRef",
                ref: bannerRef,
                class: _normalizeClass(['ai-chat-banner', { expanded: cardShape.value }]),
                onClick: onBannerClick
            }, [
                _createElementVNode("div", {
                    class: "ai-chat-header",
                    onClick: _cache[1] || (_cache[1] = _withModifiers(() => { }, ["stop"]))
                }, [
                    _createElementVNode("img", {
                        src: _unref(isDarkMode) ? require('@/assets/AIChat.png') : require('@/assets/AI-Chat-24px.svg'),
                        class: "ai-chat-icon",
                        alt: "AI",
                        onClick: _cache[0] || (_cache[0] = ($event) => (expanded.value ? null : expand()))
                    }, null, 8, _hoisted_1),
                    (!expanded.value && resolvedExamples.value && resolvedExamples.value.length > 0)
                        ? (_openBlock(), _createElementBlock("div", {
                            key: 0,
                            class: "ai-chat-scroll",
                            onClick: expand
                        }, [
                            _createVNode(_Transition, {
                                name: "scroll-text",
                                mode: "out-in"
                            }, {
                                default: _withCtx(() => [
                                    (_openBlock(), _createElementBlock("span", {
                                        class: "ai-chat-text",
                                        key: currentIndex.value
                                    }, _toDisplayString(resolvedExamples.value[currentIndex.value]), 1))
                                ]),
                                _: 1
                            })
                        ]))
                        : (!expanded.value)
                            ? (_openBlock(), _createElementBlock("span", {
                                key: 1,
                                class: "ai-chat-text",
                                onClick: expand
                            }, "Ask your assistant..."))
                            : (_openBlock(), _createElementBlock("span", _hoisted_2, "Ask your assistant")),
                    (expanded.value)
                        ? (_openBlock(), _createBlock(_component_v_icon, {
                            key: 3,
                            class: "close-icon",
                            onClick: _withModifiers(collapse, ["stop"])
                        }, {
                            default: _withCtx(() => _cache[6] || (_cache[6] = [
                                _createTextVNode("mdi-close")
                            ])),
                            _: 1
                        }))
                        : _createCommentVNode("", true)
                ]),
                _createVNode(_Transition, {
                    name: "slide-down",
                    onAfterEnter: onExpandedShown,
                    onAfterLeave: _cache[5] || (_cache[5] = ($event) => (cardShape.value = false))
                }, {
                    default: _withCtx(() => [
                        (expanded.value)
                            ? (_openBlock(), _createElementBlock("div", {
                                key: 0,
                                class: "expanded-content",
                                onClick: _cache[4] || (_cache[4] = _withModifiers(() => { }, ["stop"]))
                            }, [
                                _createElementVNode("div", {
                                    class: "messagesCanvas",
                                    ref_key: "canvasRef",
                                    ref: canvasRef
                                }, [
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(messages), (m) => {
                                        return (_openBlock(), _createElementBlock("div", { key: m }, [
                                            _createElementVNode("div", {
                                                class: _normalizeClass('message ' + m.direction)
                                            }, [
                                                (m.direction === 'in')
                                                    ? (_openBlock(), _createElementBlock("img", {
                                                        key: 0,
                                                        class: "message-avatar",
                                                        src: require('@/assets/mg.png'),
                                                        alt: "MG"
                                                    }, null, 8, _hoisted_3))
                                                    : _createCommentVNode("", true),
                                                _createElementVNode("div", {
                                                    class: "message-content",
                                                    innerHTML: _unref(formatMessage)(_unref(applyMonetaryMask)(m.body))
                                                }, null, 8, _hoisted_4)
                                            ], 2)
                                        ]));
                                    }), 128)),
                                    (_unref(typing))
                                        ? (_openBlock(), _createElementBlock("div", _hoisted_5, _cache[7] || (_cache[7] = [
                                            _createElementVNode("div", { class: "message in typingIndicator" }, [
                                                _createElementVNode("span", { class: "typingDots" }, [
                                                    _createElementVNode("span"),
                                                    _createElementVNode("span"),
                                                    _createElementVNode("span")
                                                ])
                                            ], -1)
                                        ])))
                                        : _createCommentVNode("", true),
                                    (activeChips.value.length > 0 && showPromptChips.value && !_unref(typing))
                                        ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(activeChips.value, (p) => {
                                                return (_openBlock(), _createElementBlock("button", {
                                                    key: p,
                                                    type: "button",
                                                    class: "prompt-chip",
                                                    onClick: ($event) => (sendPrompt(p))
                                                }, _toDisplayString(_unref(applyMonetaryMask)(p)), 9, _hoisted_7));
                                            }), 128))
                                        ]))
                                        : _createCommentVNode("", true),
                                    _createElementVNode("div", {
                                        style: { "clear": "both" },
                                        ref_key: "endRef",
                                        ref: endRef
                                    }, null, 512)
                                ], 512),
                                _createElementVNode("div", {
                                    class: "inputBarInner",
                                    ref_key: "inputBarRef",
                                    ref: inputBarRef
                                }, [
                                    _createVNode(_component_v_text_field, {
                                        modelValue: _unref(message),
                                        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => (_isRef(message) ? (message).value = $event : null)),
                                        class: _normalizeClass(["chatInput", { 'chatInputShrink': _unref(message) && _unref(message).trim() !== '' }]),
                                        type: "text",
                                        placeholder: "Enter your message",
                                        onKeypress: _unref(onKeyPress),
                                        onFocus: onInputFocus,
                                        onBlur: onInputBlur,
                                        "hide-details": "",
                                        onKeyup: _withKeys(_unref(sendMessage), ["enter"]),
                                        variant: "solo",
                                        flat: true,
                                        "single-line": "",
                                        outlined: "",
                                        rounded: "",
                                        dense: "",
                                        key: "input"
                                    }, {
                                        "prepend-inner": _withCtx(() => [
                                            _createVNode(_component_v_menu, { location: "top" }, {
                                                activator: _withCtx(({ props: menuProps }) => [
                                                    _createVNode(_component_v_icon, _mergeProps(menuProps, {
                                                        class: "sendIcon",
                                                        style: { "font-size": "24px", "margin-right": "10px", "color": "#8c8897" }
                                                    }), {
                                                        default: _withCtx(() => _cache[8] || (_cache[8] = [
                                                            _createTextVNode("mdi-plus-circle-outline")
                                                        ])),
                                                        _: 2
                                                    }, 1040)
                                                ]),
                                                default: _withCtx(() => [
                                                    _createVNode(_component_v_list, {
                                                        density: "compact",
                                                        style: { "min-width": "180px" }
                                                    }, {
                                                        default: _withCtx(() => [
                                                            _createVNode(_component_v_list_item, { onClick: _unref(attach) }, {
                                                                prepend: _withCtx(() => [
                                                                    _createVNode(_component_v_icon, { style: { "color": "#8c8897" } }, {
                                                                        default: _withCtx(() => _cache[9] || (_cache[9] = [
                                                                            _createTextVNode("mdi-paperclip")
                                                                        ])),
                                                                        _: 1
                                                                    })
                                                                ]),
                                                                default: _withCtx(() => [
                                                                    _createVNode(_component_v_list_item_title, null, {
                                                                        default: _withCtx(() => _cache[10] || (_cache[10] = [
                                                                            _createTextVNode("Attach document")
                                                                        ])),
                                                                        _: 1
                                                                    })
                                                                ]),
                                                                _: 1
                                                            }, 8, ["onClick"]),
                                                            _createVNode(_component_v_list_item, {
                                                                onClick: _unref(sendToWhatsApp),
                                                                disabled: !_unref(hasUnsentMessages)
                                                            }, {
                                                                prepend: _withCtx(() => [
                                                                    _createVNode(_component_v_icon, {
                                                                        style: _normalizeStyle('color: ' + (_unref(hasUnsentMessages) ? '#25D366' : '#e2e0e8'))
                                                                    }, {
                                                                        default: _withCtx(() => _cache[11] || (_cache[11] = [
                                                                            _createTextVNode("mdi-whatsapp")
                                                                        ])),
                                                                        _: 1
                                                                    }, 8, ["style"])
                                                                ]),
                                                                default: _withCtx(() => [
                                                                    _createVNode(_component_v_list_item_title, null, {
                                                                        default: _withCtx(() => _cache[12] || (_cache[12] = [
                                                                            _createTextVNode("Save this chat to WhatsApp")
                                                                        ])),
                                                                        _: 1
                                                                    })
                                                                ]),
                                                                _: 1
                                                            }, 8, ["onClick", "disabled"])
                                                        ]),
                                                        _: 1
                                                    })
                                                ]),
                                                _: 1
                                            })
                                        ]),
                                        "append-inner": _withCtx(() => [
                                            _createVNode(_component_v_icon, {
                                                class: "sendIcon",
                                                color: _unref(message) && _unref(message).trim() !== '' ? '#8c8897' : '#e2e0e8',
                                                onClick: _cache[2] || (_cache[2] = ($event) => (_unref(message) && _unref(message).trim() !== '' ? _unref(sendMessage)() : null)),
                                                style: { "font-size": "24px" }
                                            }, {
                                                default: _withCtx(() => _cache[13] || (_cache[13] = [
                                                    _createTextVNode("mdi-send-outline")
                                                ])),
                                                _: 1
                                            }, 8, ["color"])
                                        ]),
                                        _: 1
                                    }, 8, ["modelValue", "class", "onKeypress", "onKeyup"])
                                ], 512)
                            ]))
                            : _createCommentVNode("", true)
                    ]),
                    _: 1
                })
            ], 2));
        };
    }
});
